import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  Send as SendIcon,
  Description as DescriptionIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { DocumentUploadService } from '../../services/documentUploadService';

interface DocumentPreviewPopupProps {
  open: boolean;
  onClose: () => void;
  onSend: (description: string, documentFile: File) => void;
  documentFile: File | null;
  documentPreview: string | null;
  loading: boolean;
  error: string | null;
}

const DocumentPreviewPopup: React.FC<DocumentPreviewPopupProps> = ({
  open,
  onClose,
  onSend,
  documentFile,
  documentPreview,
  loading,
  error,
}) => {
  const [description, setDescription] = useState('');
  const [documentInfo, setDocumentInfo] = useState<{
    category: string;
    icon: string;
    typeDescription: string;
    isValid: boolean;
  } | null>(null);

  // Reset state when popup opens
  useEffect(() => {
    if (open && documentFile) {
      setDescription('');
      analyzeDocument();
    }
  }, [open, documentFile]);

  const analyzeDocument = () => {
    if (!documentFile) return;

    const category = DocumentUploadService.getDocumentCategory(documentFile.type);
    const icon = DocumentUploadService.getDocumentIcon(documentFile.type);
    const typeDescription = DocumentUploadService.getFileTypeDescription(documentFile.type);
    const isValid = DocumentUploadService.isSupportedDocumentType(documentFile);

    setDocumentInfo({
      category,
      icon,
      typeDescription,
      isValid,
    });
  };

  const handleSend = () => {
    if (documentFile && documentInfo?.isValid) {
      onSend(description, documentFile);
    }
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'document': return 'primary';
      case 'presentation': return 'secondary';
      case 'spreadsheet': return 'success';
      case 'pdf': return 'error';
      case 'data': return 'warning';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'document': return '📄';
      case 'presentation': return '📊';
      case 'spreadsheet': return '📈';
      case 'pdf': return '📕';
      case 'data': return '📋';
      default: return '📄';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.2 }}
      >
        <DialogTitle sx={{ 
          pb: 1,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <DescriptionIcon sx={{ color: 'primary.main', fontSize: 28 }} />
              <Typography variant="h5" fontWeight="bold" sx={{ 
                background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}>
                Send Document
              </Typography>
            </Box>
            <Button
              onClick={onClose}
              variant="outlined"
              size="small"
              sx={{ 
                borderRadius: 2,
                minWidth: 'auto',
                px: 2,
              }}
            >
              <CloseIcon />
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, background: 'rgba(255, 255, 255, 0.6)' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              justifyContent: 'center', 
              py: 4,
              gap: 2
            }}>
              <CircularProgress size={40} />
              <Typography variant="body1">
                Uploading document...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Document Preview */}
              {documentFile && documentInfo && (
                <Card sx={{ 
                  background: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  border: '1px solid rgba(0,0,0,0.1)',
                  overflow: 'hidden'
                }}>
                  <CardContent>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 3,
                      p: 2,
                      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                      borderRadius: 1,
                      mb: 2
                    }}>
                      <Box sx={{ 
                        fontSize: '3rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 80,
                        height: 80,
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: 2,
                        border: '2px solid rgba(0,0,0,0.1)'
                      }}>
                        {getCategoryIcon(documentInfo.category)}
                      </Box>
                      
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                          {documentFile.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={documentInfo.typeDescription}
                            color={getCategoryColor(documentInfo.category) as any}
                            size="small"
                            icon={<FileIcon />}
                          />
                          <Chip
                            label={DocumentUploadService.formatFileSize(documentFile.size)}
                            variant="outlined"
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {documentFile.type}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Document Details */}
                    <Box sx={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: 2,
                      p: 2,
                      background: 'rgba(0,0,0,0.02)',
                      borderRadius: 1
                    }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          File Type
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {documentInfo.typeDescription}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          File Size
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {DocumentUploadService.formatFileSize(documentFile.size)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Extension
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          .{DocumentUploadService.getFileExtension(documentFile.name)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Category
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {documentInfo.category.charAt(0).toUpperCase() + documentInfo.category.slice(1)}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Validation Status */}
                    {!documentInfo.isValid && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        This file type is not supported. Please select a Word document, PowerPoint presentation, Excel spreadsheet, PDF, or other supported office file.
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Description Input */}
              <TextField
                label="Description (Optional)"
                placeholder="Add a description for this document..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: 2,
                  }
                }}
                helperText="Add a description to help others understand what this document contains"
              />

              {/* Supported File Types Info */}
              <Paper sx={{ 
                p: 2,
                background: 'rgba(255, 255, 255, 0.9)',
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.1)'
              }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                  Supported File Types:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {DocumentUploadService.getSupportedExtensions().map((ext) => (
                    <Chip
                      key={ext}
                      label={ext}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Maximum file size: 50MB
                </Typography>
              </Paper>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ 
          p: 3, 
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)'
        }}>
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={loading || !documentFile || !documentInfo?.isValid}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
            sx={{
              background: 'linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)',
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 'bold',
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(45deg, #1565c0 30%, #1976d2 90%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {loading ? 'Sending...' : 'Send Document'}
          </Button>
        </DialogActions>
      </motion.div>
    </Dialog>
  );
};

export default DocumentPreviewPopup;
